import { AutoAwesome, AutoFixHigh, Cancel, CheckCircle, EditCalendar, History } from "@mui/icons-material";
import { Box, Button, CardHeader, CircularProgress, Grid, IconButton, Link, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { observer, useLocalObservable } from "mobx-react";
import { toJS } from "mobx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AvailabilityRequestDto,
  BlockingSlotDto,
  GeneratedWorkflowDto,
  PatientAvailabilityDto,
  WorkflowEventName,
  WorkflowResultDtoWorkflowNameEnum,
} from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import FlatCard from "../../atoms/FlatCard";
import { MobiliAvatar } from "../../atoms/MobiliAvatar";
import useStore from "../../helpers/useStore";
import { useWorkflow } from "../../hooks/useWorkflow";
import { mobiliTheme } from "../../themes/mobiliTheme";
import ChatMessageBox from "../../molecules/ChatMessageBox";
import { AgentChatStore } from "../../stores/AgentChat/agentChatStore";
import {
  useAppointmentsByPatient,
  useBlockedSlotsByPatient,
  useCancelledAppointmentsByPatient,
  useContinuousAppointmentsByPatient,
  useInactiveAppointmentsCount,
  usePatientUnavailabilityByPatient,
} from "../../stores/Appointment/appointment.queries";
import { CalendarCommands } from "../../stores/Calendar/calendar.commands";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { usePatient } from "../../stores/Patients";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { UserContext } from "../../stores/User/User.provider";
import TerminView from "../PatientOverview/RxInfo/TerminView";
import { TerminViewConstants } from "../PatientOverview/TerminView";
import CancellationHistoryDialog from "../PatientOverview/PatientHistory/CancellationHistoryDialog";
import AppointmentSuggestion from "./Appointments/AppointmentSuggestion";
import AIAvailabilityTextField from "./Availability/AIAvailabilityTextField";
import PatientAvailability from "./Availability/PatientAvailability";
import PatientUnavailability from "./Availability/PatientUnavailability";
import TherapistList from "./TherapistList/TherapistList";
import NewRx from "./NewRx";
import UnscheduledAppointments from "./UnscheduledAppointments";
import { useReschedulingAgent } from "../../hooks/useReschedulingAgent";

const cardStyles = {
  card1: {
    // backgroundColor: (theme:Theme) => theme.palette.primary.light,
    margin: "10px",
  },
  card2: {
    // backgroundColor: "white",
    margin: "10px",
  },
  card3: {
    // backgroundColor: "white",
    margin: "10px",
  },
};

const therapyRxs: any[] = [
  {
    id: "1",
    therapyRx: ["KG", "MT", "KG"],
    start: "09:00",
    end: "10:00",
  },
  {
    id: "2",
    therapyRx: ["KT"],
    start: "11:00",
    end: "12:00",
  },
  {
    id: "3",
    therapyRx: ["MT", "KG"],
    start: "02:00",
    end: "03:00",
  },
  {
    id: "3",
    therapyRx: ["BT"],
    start: "02:00",
    end: "03:00",
  },
];

const ScheduleOverview: React.FC = observer(() => {
  const PatientsStore = useStore(PatientsContext);
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const canSeeFeaturePreview = UserStore.hasPermission("featurePreview");

  // Convert patientData to number for APIs that need it
  const patientId = PatientsStore.selectedPatientId;
  const { data: patientData } = usePatient(patientId!);

  // Use React Query hooks instead of store methods
  const appointmentsQuery = useAppointmentsByPatient(patientId!);
  const continuousAppointmentsQuery = useContinuousAppointmentsByPatient(patientId!);
  const cancelledAppointmentsQuery = useCancelledAppointmentsByPatient(patientId!);
  const blockedSlotsQuery = useBlockedSlotsByPatient(patientId!);
  const patientUnavailabilityQuery = usePatientUnavailabilityByPatient(patientId!);
  const inactiveCountQuery = useInactiveAppointmentsCount(patientId!);

  const { appointments: appointmentsApi } = useApi();

  // Extract data from queries
  const appointments = appointmentsQuery.data || [];
  const continousAppointments = continuousAppointmentsQuery.data || [];
  const cancelledAppointmentsList =
    cancelledAppointmentsQuery.data?.map((cancelled: any) => cancelled.appointment) || [];
  const blockedData = blockedSlotsQuery.data || [];
  const patientAvailability = patientUnavailabilityQuery.data || [];
  const inActiveCount = inactiveCountQuery.data || null;

  // Loading state - true if any critical query is loading
  const appointmentsLoading = appointmentsQuery.isLoading || continuousAppointmentsQuery.isLoading;

  const unscheduledApps = appointments.filter((appointment) => !appointment.start);
  const scheduledApps = appointments.filter((appointment) => appointment.start);
  const [isSuggestionDialogOpen, setsuggestionDialogOpen] = useState(false);
  const [isCancellationHistoryOpen, setIsCancellationHistoryOpen] = useState(false);
  const navigate = useNavigate();
  const calendarStore = useStore(CalendarContext);
  const { navigateToCalendar } = CalendarCommands(calendarStore);
  const { startRescheduling } = useReschedulingAgent(patientId);
  const openSuggestionDialog = () => {
    setsuggestionDialogOpen(true);
  };

  const handlePlanning = (patientData: any) => {
    navigateToCalendar(navigate, {
      patientId: patientId,
      patientLastName: patientData?.lastName,
    });
  };

  const unscheduledContinuousApps = continousAppointments.filter(
    (appointment) => !appointment.start,
  );
  const scheduledContinuousApps = continousAppointments.filter((appointment) => appointment.start);

  // State for supporting child components
  const [setblockedData] = useState(() => () => {}); // Placeholder
  const [setPatientAvailability] = useState(() => () => {}); // Placeholder
  const [availabilityRequest, setAvailabilityRequest] = useState<AvailabilityRequestDto>({
    availabilities: [],
    blockedSlots: [],
  });
  const [availabilityDraft, setAvailabilityDraft] = useState("");

  const [disabled, setDisabled] = useState(false);
  const chat = useLocalObservable(() => new AgentChatStore());

  // --- LLM draft state: holds AI-suggested data for human review before saving ---
  const [aiDraftAvailabilities, setAiDraftAvailabilities] = useState<
    PatientAvailabilityDto[] | undefined
  >(undefined);
  const [draftSaveCounter, setDraftSaveCounter] = useState(0);
  const [draftRejectCounter, setDraftRejectCounter] = useState(0);
  const [aiDraftBlockedSlots, setAiDraftBlockedSlots] = useState<BlockingSlotDto[] | undefined>(
    undefined,
  );
  // Once user accepts/rejects drafts, block further events from re-injecting
  const draftHandledRef = useRef(false);

  // Reset chat and draft state when patient changes
  useEffect(() => {
    chat.clear();
    setAiDraftAvailabilities(undefined);
    setAiDraftBlockedSlots(undefined);
    setDraftSaveCounter(0);
    setDraftRejectCounter(0);
    setAvailabilityDraft("");
    setAvailabilityRequest({ availabilities: [], blockedSlots: [] });
    setDisabled(false);
    draftHandledRef.current = false;
  }, [patientId]);

  // --- useWorkflow: subscribe to LLM availability processing results via WebSocket ---
  const {
    workflowLoading: isProcessingAvailability,
    subscribeToWorkflow: subscribeToAvailabilityWorkflow,
  } = useWorkflow({
    workflowName: WorkflowResultDtoWorkflowNameEnum.PatientAvailabilityRequestWorkflow,
    onResult: (resultDto) => {
      const result = resultDto.data;

      // if (result?.patientAvailabilities?.length) {
      //   setAiDraftAvailabilities(result.patientAvailabilities);
      // }
      // if (result?.patientBlockedSlots?.length) {
      //   setAiDraftBlockedSlots(
      //     result.patientBlockedSlots.map((slot) => ({
      //       id: slot.id,
      //       start: slot.start,
      //       end: slot.end,
      //     })),
      //   );
      // }
      // Remove loading message and add result
      chat.removeLoading();
      const parts: string[] = [];
      if (result?.patientAvailabilities?.length) {
        parts.push(`${result.patientAvailabilities.length} Verfügbarkeit(en) erkannt`);
      }
      if (result?.patientBlockedSlots?.length) {
        parts.push(`${result.patientBlockedSlots.length} Sperrzeit(en) erkannt`);
      }
      chat.add({
        role: "assistant",
        content: parts.length ? parts.join(" und ") + "." : "Fertig — keine Änderungen erkannt.",
        timestamp: new Date(),
        collapsibleLabel: "Rohdaten anzeigen",
        collapsibleContent: (
          <pre style={{ fontSize: "0.7rem", margin: 0, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        ),
      });
    },
    onProgress: (progressDto) => {
      if (progressDto?.message) {
        chat.add({
          role: "assistant",
          content: `${progressDto.message}`,
          timestamp: new Date(),
        });
      }
    },
    onWorkflowEvent(eventDto) {
      if (eventDto?.eventName === WorkflowEventName.PatientAvailabilityLlmStepParsed) {
        // const llmEvent = eventDto as WorkflowEventLlmStepDto;
        const step = eventDto.data;

        // const content = step.outputSummary || step.inputSummary || step.stepName;

        const content = step.explanation || step.stepName;
        chat.add({
          role: "assistant",
          content,
          timestamp: new Date(),
          collapsibleLabel: `${step.stepName}${step.durationMs ? ` (${step.durationMs}ms)` : ""}`,
          collapsibleContent: (
            <Box sx={{ fontSize: "0.75rem" }}>
              <Typography variant="caption" display="block">
                <b>Schritt:</b> {step.stepName}
              </Typography>
              <Typography variant="caption" display="block">
                <b>Input:</b> {step.input}
              </Typography>
              {step.output && (
                <Typography variant="caption" display="block">
                  <b>Output:</b> {JSON.stringify(step.output)}
                </Typography>
              )}
              {/* {step.explanation && (
                <Typography variant="caption" display="block">
                  <b>Erklärung:</b> {step.explanation}
                </Typography>
              )} */}
              {step.llmModel && (
                <Typography variant="caption" display="block">
                  <b>Modell:</b> {step.llmModel}
                </Typography>
              )}
              {step.errorMessage && (
                <Typography variant="caption" display="block" color="error">
                  <b>Fehler:</b> {step.errorMessage}
                </Typography>
              )}
              {step.metadata && Object.keys(step.metadata).length > 0 && (
                <pre style={{ fontSize: "0.65rem", margin: "4px 0 0", whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(step.metadata, null, 2)}
                </pre>
              )}
            </Box>
          ),
        });

        const availabilitiesOutput = step.output;
        if (!draftHandledRef.current) {
          if (availabilitiesOutput?.patientAvailabilities?.length) {
            // Functional update: only inject if state is still undefined (first event wins)
            setAiDraftAvailabilities((prev) => prev ?? availabilitiesOutput.patientAvailabilities);
          }
          if (availabilitiesOutput?.patientBlockedSlots?.length) {
            // Functional update: only inject if state is still undefined (first event wins)
            setAiDraftBlockedSlots(
              (prev) =>
                prev ??
                availabilitiesOutput.patientBlockedSlots.map((slot) => ({
                  id: slot.id,
                  start: slot.start,
                  end: slot.end,
                })),
            );
          }
        }
      }
    },
    onError: (errorMessage) => {
      console.error("Availability workflow error:", errorMessage);
      chat.removeLoading();
      chat.add({
        role: "system",
        content: `Fehler: ${errorMessage}`,
        timestamp: new Date(),
      });
      setDisabled(false);
    },
  });

  // Callbacks for children to clear draft state after save/dismiss
  const handleAiDraftAvailabilitiesClear = useCallback(() => {
    setAiDraftAvailabilities(undefined);
    draftHandledRef.current = true;
  }, []);
  const handleAiDraftBlockedSlotsClear = useCallback(() => {
    setAiDraftBlockedSlots(undefined);
    draftHandledRef.current = true;
  }, []);

  const handleRejectDrafts = useCallback(() => {
    setDraftRejectCounter((c) => c + 1);
    draftHandledRef.current = true;
    // Also clear parent draft state directly (handles case where child rows were already deleted)
    setAiDraftAvailabilities(undefined);
    setAiDraftBlockedSlots(undefined);
    chat.add({
      role: "system",
      content: "Vorschläge abgelehnt.",
      timestamp: new Date(),
    });
  }, [chat]);

  const handleAppointmentSuggestionClose = (wasSaved: boolean) => {
    setsuggestionDialogOpen(false);
    if (wasSaved) {
      // Refetch queries when data changes
      appointmentsQuery.refetch();
      continuousAppointmentsQuery.refetch();
      cancelledAppointmentsQuery.refetch();
      blockedSlotsQuery.refetch();
      patientUnavailabilityQuery.refetch();
      inactiveCountQuery.refetch();
    }
  };

  // Only update local draft on change
  const handleAvailabilityChange = (requestString: string) => {
    setAvailabilityDraft(requestString);
  };

  // Only submit to backend on blur or Enter
  const handleAvailabilitySubmit = async (requestString: string) => {
    const trimmed = requestString?.trim();
    if (!trimmed) return; // Do not submit if empty or only whitespace

    // Build request with requestString only if it has a value
    const updatedRequest: AvailabilityRequestDto = {
      ...availabilityRequest,
      requestString: trimmed,
    };

    setAvailabilityRequest(updatedRequest);
    setDisabled(true);

    // Add user message to chat
    chat.add({
      role: "user",
      content: trimmed,
      timestamp: new Date(),
    });
    // Add loading placeholder
    chat.add({
      role: "assistant",
      content: "",
      isLoading: true,
    });

    try {
      const job = (await appointmentsApi.scheduleJobForPatientAvailability(
        patientId!,
        updatedRequest,
      )) as GeneratedWorkflowDto;
      if (job?.workflowId) {
        draftHandledRef.current = false;
        subscribeToAvailabilityWorkflow(job);
      }
      // Reset input field
      setAvailabilityDraft("");
      setAvailabilityRequest({ availabilities: [], blockedSlots: [] });
    } catch (error) {
      console.error("Error processing availability request:", error);
      chat.removeLoading();
      chat.add({
        role: "system",
        content: "Fehler beim Senden der Anfrage.",
        timestamp: new Date(),
      });
    } finally {
      setDisabled(false);
    }
  };

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       if (patientId) {
  //         const response = await getRxWithNoAppointments(patientId);
  //         setRxnoAppointments(response);
  //       }
  //     } catch (error) {
  //       console.log("error", error);
  //     }
  //   };

  //   fetchData();
  // }, [patientData, user, getRxWithNoAppointments, patientId]);

  // Define an array of background colors
  return (
    // <Container maxWidth="xl" style={{ border: '1px solid red' }}>
    <Grid container spacing={2}>
      {/*Single Element: Termine */}
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={cardStyles.card2}>
          <CardHeader title="Dauertermine" />
          <CardContent>
            <TerminView
              appointments={scheduledContinuousApps}
              patientId={patientId!}
              appointmentsLoading={appointmentsLoading}
              isView={TerminViewConstants.CONTINOUS_TERMIN}
              isContinuous={true}
            />

            {/* <ComponentWithData prop3="Value3" prop4="Value4" /> */}
          </CardContent>
        </Card>

        <Card sx={cardStyles.card2}>
          <CardHeader title="Termine" />
          <CardContent>
            {/* <Termin
              appointments={scheduledApps}
              setDataRefreshFlag={setDataRefreshFlag}
              patientId={patientData}
              dataRefreshFlag={dataRefreshFlag}
              blockedData={blockedData}
              setblockedData={setblockedData}
              patientAvailabilityData={patientAvailability}
              setPatientAvailability={setPatientAvailability}
            />  */}
            <TerminView
              appointments={scheduledApps}
              patientId={patientId!}
              appointmentsLoading={appointmentsLoading}
              isView={TerminViewConstants.TERMIN_VIEW}
              inActiveCount={inActiveCount}
            />
            {/* <ComponentWithData prop3="Value3" prop4="Value4" /> */}
          </CardContent>
        </Card>
      </Grid>

      {/* Two Elements : Templates and Avaialbility */}
      <Grid item xs={12} sm={6} md={6}>
        {/* Neu Rezepte Card */}
        <Card sx={cardStyles.card1}>
          <CardHeader title="Terminformat für neue Rezepte" />
          <CardContent>
            {/* Keyed on the patient: NewRx seeds local draft state (address,
                visit fee, selected therapies) from the patient and does not
                reset it on prop change. Without the remount the previous
                patient's address stayed selected and was written onto the next
                patient's Dauertermine (MDprod, Jan–Aug 2026). */}
            <NewRx key={patientId ?? "none"} patientId={patientId!} />
            {/* <ComponentWithData prop1="Value1" prop2="Value2" /> */}
          </CardContent>
        </Card>
        {/* <Grid item xs={12} sm={6} md={20}> */}

        {/* Verfügbarkeit Card */}
        <Card sx={cardStyles.card3}>
          <CardHeader title="Zeitliche Einschränkungen" />
          <Typography variant="body2" color="text.secondary">
            <CardContent>
              <FlatCard title="Wöchentlich">
                <PatientUnavailability
                  patientId={patientId!}
                  patientAvailabilityData={patientAvailability}
                  aiDraftAvailabilities={aiDraftAvailabilities}
                  onAiDraftClear={handleAiDraftAvailabilitiesClear}
                  triggerSave={draftSaveCounter}
                  triggerReject={draftRejectCounter}
                />
              </FlatCard>

              <FlatCard title="Einmalig">
                <PatientAvailability
                  patientId={patientId!}
                  blockedData={blockedData}
                  aiDraftBlockedSlots={aiDraftBlockedSlots}
                  onAiDraftClear={handleAiDraftBlockedSlotsClear}
                  triggerSave={draftSaveCounter}
                  triggerReject={draftRejectCounter}
                />
              </FlatCard>

              {chat.messages.length > 0 && (
                <Box sx={{ mt: 2, position: "relative" }}>
                  <ChatMessageBox
                    messages={toJS(chat.messages)}
                    maxHeight={300}
                    userAvatar={
                      user ? (
                        <MobiliAvatar
                          user={user}
                          sx={{ width: 22, height: 22, fontSize: "0.6rem" }}
                        />
                      ) : undefined
                    }
                    slotProps={{
                      bubbleBox: { px: 1, py: 0.5 },
                      content: { fontSize: "0.75rem" },
                      timestamp: { fontSize: "0.55rem" },
                      avatarSize: 22,
                    }}
                  />
                </Box>
              )}

              {(aiDraftAvailabilities || aiDraftBlockedSlots) && (
                <Box
                  sx={{
                    mt: 1.5,
                    px: 2,
                    py: 1.5,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      color: "warning.dark",
                      fontWeight: 500,
                      fontSize: "0.82rem",
                    }}
                  >
                    ⚠️ Die KI-Vorschläge können fehlerhaft sein — bitte sorgfältig prüfen, bevor Sie
                    übernehmen.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Cancel />}
                      onClick={handleRejectDrafts}
                    >
                      Ablehnen
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckCircle />}
                      onClick={() => {
                        draftHandledRef.current = true;
                        setDraftSaveCounter((c) => c + 1);
                      }}
                    >
                      Übernehmen
                    </Button>
                  </Box>
                </Box>
              )}

              <Box display="flex" flexDirection="row" alignItems="center" sx={{ mt: 2 }}>
                <AIAvailabilityTextField
                  placeholder="Hinweis zur Verfügbarkeit (z.B. nur nach 14 Uhr, mittwochs nicht verfügbar)"
                  request={{ ...availabilityRequest, requestString: availabilityDraft }}
                  onChange={handleAvailabilityChange}
                  onSubmit={handleAvailabilitySubmit}
                  disabled={
                    disabled ||
                    isProcessingAvailability ||
                    !!(aiDraftAvailabilities || aiDraftBlockedSlots)
                  }
                />
                {isProcessingAvailability && <CircularProgress sx={{ fontSize: 30, ml: 1 }} />}
              </Box>
            </CardContent>
            {/* <ComponentWithData prop5="Value5" prop6="Value6" /> */}
          </Typography>
        </Card>

        {/* Therapeuten White-/Blacklist Card */}
        <Card sx={cardStyles.card3}>
          <CardHeader title="Therapeutenwünsche" />
          <CardContent>
            <Box
              sx={{
                backgroundColor: "white",
                border: `1px solid ${mobiliTheme.mainHeader}`,
                borderRadius: 2,
                p: 2,
              }}
            >
              <TherapistList patientId={patientId!} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={cardStyles.card2}>
            <CardHeader
              title="Stornierung"
              action={
                <Tooltip title="Stornierungsverlauf anzeigen">
                  <IconButton
                    aria-label="Stornierungsverlauf anzeigen"
                    onClick={() => setIsCancellationHistoryOpen(true)}
                    sx={{ color: mobiliTheme.mainHeader }}
                  >
                    <History />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              {cancelledAppointmentsList.length > 0 && (
                <Grid
                  item
                  xs={"auto"}
                  // alignSelf="flex-end"
                  sx={{ border: "0px red solid", marginTop: 2 }}
                >
                  <Button
                    variant="contained"
                    sx={{ marginRight: 1 }}
                    onClick={openSuggestionDialog}
                    startIcon={<AutoAwesome />}
                  >
                    KI
                  </Button>
                  {canSeeFeaturePreview && (
                    <Tooltip title="Planungs-Agent">
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ marginRight: 1, minWidth: "auto" }}
                        onClick={() => {
                          startRescheduling();
                          handlePlanning(patientData);
                        }}
                      >
                        <AutoFixHigh />
                        Plan KI
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handlePlanning(patientData)}
                    startIcon={<EditCalendar />}
                  >
                    Planen
                  </Button>
                </Grid>
              )}
              {cancelledAppointmentsList.length > 0 ? (
                <TerminView
                  appointments={cancelledAppointmentsList}
                  patientId={patientId!}
                  appointmentsLoading={appointmentsLoading}
                  isView={TerminViewConstants.CANCELLED_TERMIN}
                />
              ) : (
                <Typography sx={{ mt: 1, color: "text.secondary", fontSize: "0.9em" }}>
                  Keine offenen Stornierungen.{" "}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => setIsCancellationHistoryOpen(true)}
                    sx={{
                      verticalAlign: "baseline",
                      color: mobiliTheme.mainHeader,
                      fontWeight: 600,
                      textDecorationColor: mobiliTheme.mainHeader,
                    }}
                  >
                    Verlauf anzeigen
                  </Link>
                </Typography>
              )}
              <AppointmentSuggestion
                open={isSuggestionDialogOpen}
                onClose={handleAppointmentSuggestionClose}
                patientId={patientId!}
                blockedData={blockedData}
                setblockedData={setblockedData}
                patientAvailabilityData={patientAvailability}
                setPatientAvailabilityData={setPatientAvailability}
              />

              {/* <ComponentWithData prop3="Value3" prop4="Value4" /> */}
            </CardContent>
          </Card>
        <CancellationHistoryDialog
          open={isCancellationHistoryOpen}
          onClose={() => setIsCancellationHistoryOpen(false)}
          patientId={patientId!}
        />
        {(unscheduledApps.length > 0 || unscheduledContinuousApps.length > 0) && (
          <Card sx={cardStyles.card3}>
            <CardHeader title="Unterminierte Behandlungen" />
            <CardContent>
              <UnscheduledAppointments
                appointments={unscheduledApps}
                continuousAppointments={unscheduledContinuousApps}
                patientId={patientId!}
                cancelledAppointmentsList={cancelledAppointmentsList}
                blockedData={blockedData}
                setblockedData={setblockedData}
                patientAvailabilityData={patientAvailability}
                setPatientAvailability={setPatientAvailability}
                appointmentsLoading={appointmentsLoading}
              />
              {/* <ComponentWithData prop3="Value3" prop4="Value4" /> */}
            </CardContent>
          </Card>
        )}
      </Grid>
    </Grid>
    // </Container>
    //     <Box style={{ display: 'flex', flexDirection: 'row' }}>
    //    <Card
    //      style={{ ...cardStyles.base, ...cardStyles.card1 }}
    //   >
    //     <CardContent>
    //       <Typography variant="h6" component="div">
    //         Neu Rezepte
    //       </Typography>
    //       <Typography variant="body2" color="text.secondary">
    //         <NewRx masterData={masterData} rxnoAppointments={rxnoAppointments}/>
    //         {/* <ComponentWithData prop1="Value1" prop2="Value2" /> */}
    //       </Typography>
    //     </CardContent>
    //   </Card>

    //   <Card
    //     style={{ ...cardStyles.base, ...cardStyles.card2 }}
    //   >
    //     <CardContent>
    //       <Typography variant="h5" component="div">
    //         Termine
    //       </Typography>
    //       <Typography variant="body2" color="text.secondary">
    //         <Termin therapies={therapies}/>
    //         {/* <ComponentWithData prop3="Value3" prop4="Value4" /> */}
    //       </Typography>
    //     </CardContent>
    //   </Card>

    //   <Card
    //     style={{ ...cardStyles.base, ...cardStyles.card3 }}
    //   >
    //     <CardContent>
    //       <Typography variant="h5" component="div">
    //         Card 3
    //       </Typography>
    //       <Typography variant="body2" color="text.secondary">
    //         {/* <ComponentWithData prop5="Value5" prop6="Value6" /> */}
    //       </Typography>
    //     </CardContent>
    //   </Card>
    // </Box>
  );
});

export default ScheduleOverview;
